import React from "react";
import moment from "moment";

import { makeStyles } from "@material-ui/styles";
import ExcelIcon from "mdi-material-ui/FileExcel";

import {
  List,
  TextField,
  FunctionField,
  SelectInput,
  SelectArrayInput,
  ReferenceArrayInput,
  AutoCompleteArrayInput,
  Filter,
  FilterButton,
  ShowButton,
  CreateButton,
  DeleteButton,
  Button
} from "jazasoft";

import Datagrid from "../../../components/Datagrid";

import hasPrivilege from "../../../utils/hasPrivilege";
import useMutation from "../../../hooks/useMutation";
import { choicesEstimationState } from "../../../constants";

const inputOptions = {
  xs: 12,
  fullWidth: true,
  options: { fullWidth: true }
};

const filters = (
  <Filter>
    <SelectInput label="Status" source="state" choices={choicesEstimationState} {...inputOptions} />
    <SelectInput
      label="Checklist Type"
      source="checklistType"
      choices={[
        { id: "Costing", name: "Costing" },
        { id: "Estimation", name: "Estimation" }
      ]}
      {...inputOptions}
    />
    <ReferenceArrayInput
      label="Season"
      resource="seasons"
      source="seasonId"
      reference="seasons"
      pageSize={100}
      sort={{ field: "name", order: "asc" }}
      {...inputOptions}>
      <SelectArrayInput />
    </ReferenceArrayInput>
    <ReferenceArrayInput
      label="Product"
      resource="products"
      source="productId"
      reference="products"
      pageSize={100}
      sort={{ field: "name", order: "asc" }}
      {...inputOptions}>
      <SelectArrayInput />
    </ReferenceArrayInput>
    <ReferenceArrayInput
      label="Customer"
      resource="customers"
      source="customerId"
      reference="customers"
      sort={{ field: "name", order: "asc" }}
      {...inputOptions}>
      <AutoCompleteArrayInput disablePortal={true} />
    </ReferenceArrayInput>
  </Filter>
);

const useStyles = makeStyles({
  button: {
    padding: 0,
    margin: 0
  }
});

const DownloadButton = ({ download, busy, record = {} }) => (
  <Button
    label="Download"
    showLabel={false}
    variant="contained"
    style={{ color: "green", padding: 0, margin: 0 }}
    disabled={busy}
    onClick={download(record.id)}>
    <ExcelIcon />
  </Button>
);

const CuChecklist = props => {
  const { roles, hasAccess } = props;
  const classes = useStyles();

  const [mutate, { loading: busy }] = useMutation({ rawResponse: true });

  const download = id => () => {
    const options = {
      url: `cuChecklists/${id}`,
      method: "get",
      responseType: "arraybuffer",
      params: { action: "download" }
    };
    mutate(options, {
      onSuccess: response => {
        const header = response.headers["content-disposition"];
        var filename = header ? header.match(/filename="(.+)"/)[1] : `CuChecklist-${id}.xlsx`;

        let blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        let link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      }
    });
  };

  return (
    <List
      searchKeys={["checklistId", "createdBy"]}
      actions={({ basePath, roles, hasAccess, resource }) => (
        <div>
          {hasPrivilege(roles, hasAccess, "cu_checklist", "write") && <CreateButton basePath={basePath} showLabel={false} />}
          <FilterButton showLabel={false} resource={resource} />
        </div>
      )}
      filters={filters}
      parse={({ customerId, seasonId, productId, ...rest }) => ({
        ...rest,
        "customer.id": customerId,
        "season.id": seasonId,
        "product.id": productId
      })}
      // filterDefaultValues={roles?.includes(Role.CUT_PLANNER) ? { state: EstimationState.PENDING } : {}}
      {...props}>
      <Datagrid classes={{ headerCell: classes.headerCell, rowCell: classes.rowCell }}>
        <TextField source="checklistId" />
        <TextField source="customer.name" />
        <TextField source="season.name" />
        <TextField source="product.name" />
        <TextField source="styleCount" />
        <TextField source="checklistType" />
        <TextField source="state" />
        <FunctionField source="createdDate" render={record => `${moment(record.createdDate).format("ll")}`} />
        <TextField source="createdBy" />
        <ShowButton showLabel={false} cellClassName={classes.button} />
        {hasPrivilege(roles, hasAccess, "cu_checklist", "delete") && (
          <DeleteButton showLabel={false} alertMessage="Delete CU Checklist?" recordName="Cu Checklist" cellClassName={classes.button} />
        )}
        <DownloadButton download={download} busy={busy} />
      </Datagrid>
    </List>
  );
};

export default CuChecklist;
